import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

// Api's
import { quoteApi } from '@/main/api';

// Redux
import {
  getBodyshops,
  resetReducerBodyshops,
  useBodyshopReducer
} from '@/main/store/bodyshop';
import {
  getInsurers,
  resetReducerInsurers,
  useInsurerReducer
} from '@/main/store/insurer';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { getCartStatus, useCartStatusReducer } from '@/main/store/cartStatus';
import {
  getCartCountStatus,
  useCartCountStatusReducer
} from '@/main/store/cartCountStatus';
import { getStatesUser, useStateReducer } from '@/main/store/state';
import { getPredefinedSearch, usePredefinedSearchReducer } from '@/main/store/predefinedSearch';

// Models
import { KeyValue, KeyValueEnum } from '@/main/models/common';
import { BodyShopFilterRequest } from '@/main/models/request/bodyshopRequest';
import { InsurerFilterRequest, StateFilterRequest } from '@/main/models/request';
import {
  QuoteCartsAnswerPendingResponse,
  VehicleAutoCompleteResponse
} from '@/main/models/response';
import { QuoteCartsAnswerPendingRequest } from '@/main/models/request/quoteRequest';

// Utils & Enums
import {
  getTranslateByKey,
  isNullUndefined,
  isShowControlByElementId,
  removeEmptyValues
} from '@/main/services/utils';
import {
  initalDateForm,
  onLoadDateRangePicker,
  validateFormDate
} from '@/main/services/utilsDate';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { VehicleAutoCompleteTypeEnum } from '@/main/services/enum/VehicleAutoCompleteTypeEnum';
import { StatusCartEnum } from '@/main/services/enum/StatusCartEnum';
import { formatDateToISOString } from '@/main/services/utilsDate';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import { withQueryparams } from '@/main/components/core/queryParams/queryparams';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';
import { Controls } from '@/main/services/constants/constantsControl';
import { deleteUserPredefinedSearch, formSchema, getDetailsUserPredefinedSearch, saveUserPredefinedSearch } from './searchPendingToAnswerUtils';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';

// Components
import Box from '@/main/components/shared/box';
import Button from '@/main/components/shared/button/button';
import { AreaButton, KOGAreaButtons } from '@/main/components/shared/styles';
import KOGCardStatus from '@/main/components/shared/cardStatus/cardStatus';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import PendingSearchGrid from './components/pendingSearchGrid';
import VehicleAutoCompleteComponent from '@/main/components/shared/vehicle/VehicleAutoCompleteComponent';
import KogKeyboardDatePickerContainer from '@/main/components/shared/datepicker/keyboardDatePickerContainer';
import SearchIcon from "@mui/icons-material/Search";
import KogAutoCompleteControlledWithDelete from '@/main/components/shared/inputAutocompleteDelete/autoCompleteControlledWithDelete';
import ModalDeletePredefinedSearch from '@/main/components/shared/modalPredefinedSearch/ModalDeletePredefinedSearch';
import KOGAutoCompleteCheckbox from '@/main/components/shared/inputAutoCompleteCheck';
import ModalSavePredefinedSearch from '@/main/components/shared/modalPredefinedSearch/ModalSavePredefinedSearch';
import RenderTooltipMessage from '@/main/components/shared/renderTooltipMessage/renderTooltipMessage';

// Styles
import * as S from './styles';
import { toast } from 'react-toastify';

export interface ISearchCartForm {
  licenseOrClaimNr: string;
  startDate: Date;
  finalDate: Date;
  idCartStatus: KeyValue | null;
  insurer: KeyValue | null;
  bodyshopData: KeyValue[];
  subModel: string;
  guidVehicleManufacturer: string;
  guidVehicleModel: string;
  vehicle: string;
  stateData: KeyValue[];
  predefinedSearch: KeyValue | null;
}

export interface IGridData {
  data: QuoteCartsAnswerPendingResponse[];
  totalItems: number;
  isLoading: boolean;
}

function CartScreen() {
  const dispatch = useDispatch();
  // Reducers
  const insurerReducer = useInsurerReducer();
  const countryReducer = useCountryReducer();
  const bodyshopReducer = useBodyshopReducer();
  const { cartStatus: cartStatusReducer } = useCartStatusReducer();
  const {
    cartCountStatus: cartCountStatusReducer
  } = useCartCountStatusReducer();
  const parameterRangeDays = useParameterByTypeScreenReducer(
    ParameterTypeEnum.DefaultSearchCartDaysInterval
  );
  const stateReducer = useStateReducer();
  const { domainPredefinedSearch } = usePredefinedSearchReducer();
  const INITIAL_FORM_STATE: ISearchCartForm = {
    licenseOrClaimNr: '',
    startDate: initalDateForm(parameterRangeDays),
    finalDate: initalDateForm(),
    idCartStatus: null,
    insurer: null,
    bodyshopData: [],
    subModel: '',
    guidVehicleManufacturer: '',
    guidVehicleModel: '',
    vehicle: '',
    stateData: [],
    predefinedSearch: null
  };
  const {
    control,
    queryParamsValues,
    customHandleSubmit,
    setValue,
    clearErrors,
    reset,
    getValues,
    watch
  } = useFormQueryParams<ISearchCartForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      bodyshopData: bodyshopReducer.bodyshops,
      idCartStatus: cartStatusReducer,
      insurer: insurerReducer.insurers,
      stateData: stateReducer.statesUser,
      predefinedSearch: domainPredefinedSearch
    },
    onSubmit: onSubmit
  });

  // States
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const INITIAL_GRID_STATE: IGridData = {
    totalItems: 0,
    isLoading: false,
    data: []
  };
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
  const [formFilters, setFormFilters] = useState<QuoteCartsAnswerPendingRequest | null>(
    null
  );
  const [hasClear, setHasClear] = useState<boolean>(false);
  const [isInitialLoadScreen, setIsInitialLoadScreen] = useState<boolean>(
    false
  );
  const showPredefinedSearches = isShowControlByElementId(Controls.IdPredefinedSearchCartPendingAnswer);
  const [openModalConfirmationSavePredefinedSearch, setOpenModalConfirmationSavePredefinedSearch] = useState<boolean>(false);
  const [loadingPredefinedSearch, setloadingPredefinedSearch] = useState<boolean>(false);
  const [openModalConfirmationDeletePredefinedSearch, setOpenModalConfirmationDeletePredefinedSearch] = useState<boolean>(false);
  const [predefinedSearchToDelete, setPredefinedSearchToDelete] = useState<KeyValue | null>(null);
  const bodyshopData = watch("bodyshopData")
  const stateData = watch("stateData")
  const vehicleValue = watch("vehicle");
  const insurer = watch("insurer");
  // Default options
  const optionDefaultCountryDrop =
    countryReducer.countriesUser.data !== null &&
      countryReducer.countriesUser.data.length === 1
      ? countryReducer.countriesUser.data[0]
      : null;

  // Initial load screen
  useEffect(() => {
    dispatch(getCountriesUser());
    dispatch(
      getCartStatus({
        statusEnums: `${StatusCartEnum.Pending},${StatusCartEnum.Answered}`
      })
    );
    dispatch(getPredefinedSearch(RoutesConstants.quotationCartPendingToAnswer));
  }, []);

  // Call API's related by default country
  useEffect(() => {
    if (optionDefaultCountryDrop) {
      callBodyshopApiDomain();
      callInsurerApiDomain();
      callStateDomain();
    }
  }, [optionDefaultCountryDrop?.key]);

  // Load grid
  useEffect(() => {
    if (
      !isInitialLoadScreen &&
      cartStatusReducer.data &&
      cartStatusReducer.data.length > 0
    ) {
      const { firstDateResult, secondDateResult } = onLoadDateRangePicker(
        parameterRangeDays,
        queryParamsValues.startDate,
        queryParamsValues.finalDate
      );
      setValue('startDate', firstDateResult);
      setValue('finalDate', secondDateResult);

      // Get count status header
      dispatch(
        getCartCountStatus({
          finalDate: secondDateResult,
          startDate: firstDateResult
        })
      );

      const haveQueryParams = Object.keys(queryParamsValues).length > 0;
      const initialStatus: string | undefined = setStatusPending(
        !haveQueryParams
      );
      if (initialStatus && !haveQueryParams) {
        // Get search grid
        onSubmit(
          {
            finalDate: secondDateResult,
            startDate: firstDateResult,
            idCartStatus: { key: initialStatus }
          },
          true
        );
        setIsInitialLoadScreen(true);
      }
    }
  }, [cartStatusReducer, isInitialLoadScreen]);

  // Load fields
  function callInsurerApiDomain(bodyshopList?: KeyValue[] | null) {
    resetReducerInsurers();

    const request: InsurerFilterRequest = {
      guidCountry: String(optionDefaultCountryDrop?.key),
      type: HeadQuarterBranchFilterEnum.Only_Headquarters,
      idStatusRegister: StatusRegisterEnum.Active
    };

    if (bodyshopList && bodyshopList.length > 0) {
      request.guidsBodyshop = bodyshopList.map(x => String(x.key));
    }
    dispatch(getInsurers(request));
  }

  function callBodyshopApiDomain(optionSelected: KeyValue | null = null) {
    if (optionSelected !== null) {
      const request: BodyShopFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        guidInsurer: String(optionSelected.key),
        type: HeadQuarterBranchFilterEnum.All
      };
      dispatch(getBodyshops(request));
    } else {
      const request: BodyShopFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All
      };
      dispatch(getBodyshops(request));
    }
  }

  function callStateDomain() {
    const requestState: StateFilterRequest = {
      guidCountry: String(optionDefaultCountryDrop?.key)
    }
    dispatch(getStatesUser(requestState))
  }

  // Get data
  function setStatusPending(setValueForm: boolean = true) {
    let guidStatusPending: string | undefined;

    if (cartStatusReducer.data && cartStatusReducer.data.length > 0) {
      const statusPending:
        | KeyValueEnum
        | undefined = cartStatusReducer.data.find(
          (item: KeyValueEnum) => item.enumValue === StatusCartEnum.Pending
        );
      if (statusPending && setValueForm) {
        setValue('idCartStatus', {
          key: String(statusPending.key),
          value: statusPending.value
        });

        guidStatusPending = String(statusPending.key);
      }
    }

    return guidStatusPending;
  }

  function onSubmit(
    formInputs: ISearchCartForm,
    ignoreValidateForm: boolean = false
  ) {
    if (!ignoreValidateForm)
      validateFormDate(
        formInputs.startDate,
        formInputs.finalDate,
        parameterRangeDays
      );
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });

    const requestForm: QuoteCartsAnswerPendingRequest = {
      startDate: formatDateToISOString(formInputs.startDate),
      finalDate: formatDateToISOString(formInputs.finalDate),
      licenseOrClaimNr: formInputs?.licenseOrClaimNr
        ? String(formInputs.licenseOrClaimNr)
        : null,
      bodyshopGuids: formInputs.bodyshopData?.map(x => String(x.key)),
      insurerGuid: formInputs.insurer?.key
        ? String(formInputs.insurer.key)
        : null,
      guidVehicleManufacturer: formInputs.guidVehicleManufacturer
        ? String(formInputs.guidVehicleManufacturer)
        : null,
      guidVehicleModel: formInputs.guidVehicleModel
        ? String(formInputs.guidVehicleModel)
        : null,
      guidVehicleSubModel: formInputs.subModel
        ? String(formInputs.subModel)
        : null,
      cartAnswerStatus: formInputs.idCartStatus?.key
        ? String(formInputs.idCartStatus.key)
        : null,
      guidStates: formInputs.stateData?.map(x => String(x.key)),
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30
    };

    setFormFilters(requestForm);
    const requestFormatted = removeEmptyValues(requestForm);
    quoteApi.getCartsPendingToAnswer(requestFormatted).then(response => {
      setGridData({
        isLoading: false,
        totalItems: response.data.totalNumberOfRecords,
        data: response.data.results ? response.data.results : []
      });
    });
    setShowFilters(false);
  }

  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function handleClear() {
    reset(INITIAL_FORM_STATE);
    setStatusPending();
    setHasClear(true);
    const { firstDateResult, secondDateResult } = onLoadDateRangePicker(
      parameterRangeDays
    );
    setValue('startDate', firstDateResult);
    setValue('finalDate', secondDateResult);
    clearErrors();
    setGridData(INITIAL_GRID_STATE);
    setFormFilters(null);

    if (optionDefaultCountryDrop !== null) {
      callBodyshopApiDomain();
    } else {
      dispatch(resetReducerBodyshops());
      dispatch(resetReducerInsurers());
    }
  }

  const handleClickCard = (data: KeyValue) => {
    const findDomainStatus = cartStatusReducer.data?.find(
      item => item.enumValue === data.key
    ) as KeyValue;
    setValue('idCartStatus', findDomainStatus);
    handleClear();
    let request: ISearchCartForm = getValues();
    request.idCartStatus = findDomainStatus;
    onSubmit(request);
  };

  function getStatusFiltered() {
    if (
      cartCountStatusReducer.isLoading ||
      isNullUndefined(cartCountStatusReducer.data)
    )
      return [];

    return cartCountStatusReducer.data.map(item => ({
      key: Number(item.keyStatus),
      value: item.nameStatus,
      count: item.value ?? 0
    }));
  }

  function handleOpenModalSavePredefinedSearch() {
    setOpenModalConfirmationSavePredefinedSearch(true);
  }

  function handleOpenModalDeletePredefinedSearch(option: KeyValue) {
    setPredefinedSearchToDelete(option);
    setOpenModalConfirmationDeletePredefinedSearch(true);
  }

  async function handleSavePredefinedSearch(description: string) {
    setloadingPredefinedSearch(true);

    const rawValues = getValues();

    const parsed = formSchema.safeParse(rawValues);

    if (!parsed.success) {
      setloadingPredefinedSearch(false);
      return;
    }

    const request = parsed.data;

    const result = await saveUserPredefinedSearch(request, description);

    if (result) {
      toast.success(getTranslateByKey("information.userPredefinedSearchSavedSucess"));
      dispatch(getPredefinedSearch(RoutesConstants.quotationCartPendingToAnswer));
    } else {
      toast.error(getTranslateByKey("information.userPredefinedSearchSavedError"));
    }

    setOpenModalConfirmationSavePredefinedSearch(false);
    setloadingPredefinedSearch(false);
  }

  async function handleDeletePredefinedSearch() {
    if (!predefinedSearchToDelete) return;
    setloadingPredefinedSearch(true);

    const result = await deleteUserPredefinedSearch(predefinedSearchToDelete.key.toString());

    if (result) {
      toast.success(getTranslateByKey("information.userPredefinedSearchDeletedSucess"));
      dispatch(getPredefinedSearch(RoutesConstants.quotationCartPendingToAnswer));
    } else {
      toast.error(getTranslateByKey("information.userPredefinedSearchDeletedError"));
    }

    setOpenModalConfirmationDeletePredefinedSearch(false);
    setloadingPredefinedSearch(false);
  }

  async function handleSelectedPredefinedSearch(optionSelected: KeyValue | null) {
    if (!optionSelected) return;

    const userPredefinedSearch = await getDetailsUserPredefinedSearch(
      optionSelected.key.toString()
    );
    if (!userPredefinedSearch) return;

    const currentPredefinedValue = optionSelected;

    reset({
      ...INITIAL_FORM_STATE,
      ...userPredefinedSearch,
      predefinedSearch: currentPredefinedValue
    }, { keepDefaultValues: true });

    if (userPredefinedSearch.insurer) {
      callBodyshopApiDomain(userPredefinedSearch.insurer);
    }
  }

  return (
    <ContainerScreen
      title={`${getTranslateByKey('screen.quotation')} > ${getTranslateByKey(
        'information.cartsPendingToAnswer'
      )}`}>
      <S.Container>
        <S.CardArea>
          {getStatusFiltered().map(headerStatus => (
            <KOGCardStatus
              key={headerStatus.key}
              value={headerStatus.value}
              idStatus={headerStatus.key}
              numberOfValues={headerStatus.count}
              isCartCardStatus={true}
              onClick={() => handleClickCard(headerStatus)}
            />
          ))}
        </S.CardArea>
        <AreaButton>
          <Button onClick={handleFilters}>
            {getTranslateByKey('information.filters')}
          </Button>
        </AreaButton>
        <Box
          showBox={showFilters}
          title={getTranslateByKey('information.filters')}>
          <form onSubmit={customHandleSubmit} noValidate>
            <S.Row>
              <KogInputTextControlledNew
                name="licenseOrClaimNr"
                label={`${getTranslateByKey(
                  'information.claimNumber'
                )} / ${getTranslateByKey(
                  'information.licensePlate'
                )} / ${getTranslateByKey('information.quoteNr')}`}
                control={control}
                id="id-searchCartsPendingToAnswer-txt-licenseOrClaimNr"
              />
              <KogKeyboardDatePickerContainer
                idScreen="id-searchCartsPendingToAnswer"
                nameStartDate="startDate"
                nameFinalDate="finalDate"
                parameterRangeDays={parameterRangeDays}
                control={control}
              />
              <VehicleAutoCompleteComponent
                hasClearValue={hasClear}
                setHasClearValue={setHasClear}
                handleChangeVehicle={(vehicle) => {
                  if (vehicle === null) {
                    setValue('guidVehicleManufacturer', '');
                    setValue('guidVehicleModel', '');
                    setValue('subModel', '');
                    setValue('vehicle', '');
                  } else {
                    setValue(
                      'guidVehicleManufacturer',
                      vehicle.guidVehicleManufacturer
                    );
                    setValue('guidVehicleModel', vehicle.guidVehicleModel);
                    setValue('subModel', vehicle.guidSubModel);
                    setValue('vehicle', vehicle.value);
                  }
                }}
                vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Partial}
                vehicleValueProps={vehicleValue}
              />
            </S.Row>
            <S.Row>
              <S.AutoCompleteWrapper>
                <KOGAutoCompleteCheckbox
                  label={getTranslateByKey('information.bodyshop')}
                  selectAllLabel={getTranslateByKey('information.selectAll')}
                  isLoading={bodyshopReducer.bodyshops.isLoading}
                  options={bodyshopReducer.bodyshops.data || []}
                  disableSelectAll={!insurer?.key}
                  valueCustom={bodyshopData}
                  setValueCustom={(value) => {
                    setValue("bodyshopData", value);
                  }}
                  onChangeCustom={(value, optionChanged) => {
                    if (optionChanged?.key === 'select-all') {
                      return;
                    }
                    callInsurerApiDomain(value);
                  }}
                  customInputProps={{
                    id: 'id-searchCartsPendingToAnswer-txt-bodyshop'
                  }}
                />
              </S.AutoCompleteWrapper>
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.insurer')}
                control={control}
                nameCustom="insurer"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={insurerReducer.insurers.data}
                isLoading={insurerReducer.insurers.isLoading}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  callBodyshopApiDomain(optionSelected);
                }}
                id="id-searchCartsPendingToAnswer-txt-insurer"
              />
              <S.AutoCompleteWrapper>
                <KOGAutoCompleteCheckbox
                  label={getTranslateByKey('information.deliveryAddressState')}
                  selectAllLabel={getTranslateByKey('information.selectAll')}
                  isLoading={stateReducer.statesUser.isLoading}
                  options={stateReducer.statesUser.data || []}
                  valueCustom={stateData}
                  setValueCustom={(value) => {
                    setValue("stateData", value);
                  }}
                  customInputProps={{
                    id: 'id-searchCartsPendingToAnswer-txt-state'
                  }}
                />
              </S.AutoCompleteWrapper>
            </S.Row>
            <S.Row>
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.status')}
                control={control}
                nameCustom="idCartStatus"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={cartStatusReducer.data}
                isLoading={cartStatusReducer.isLoading}
                onChangeCustom={() => { }}
                id="id-searchCartsPendingToAnswer-txt-idQuoteStatus"
              />
              {showPredefinedSearches &&
                <>
                  <KogAutoCompleteControlledWithDelete
                    label={getTranslateByKey('information.predefinedSearches')}
                    control={control}
                    nameCustom="predefinedSearch"
                    rulesCustom={{ required: false }}
                    errorMessageCustom=""
                    hasErrorCustom={false}
                    options={domainPredefinedSearch.data}
                    isLoading={domainPredefinedSearch.isLoading}
                    onChangeCustom={(optionSelected: KeyValue | null) => {
                      handleSelectedPredefinedSearch(optionSelected);
                    }}
                    id="id-searchCartsPendingToAnswer-predefinedSearches"
                    onDeleteCustom={(option: KeyValue) => {
                      handleOpenModalDeletePredefinedSearch(option);
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <RenderTooltipMessage 
                      tooltipMessage={getTranslateByKey("information.predefinedSearchTooltipMessage")}
                    >
                        <S.PredefinedSearchLabel
                          onClick={handleOpenModalSavePredefinedSearch}
                          control={<SearchIcon  fontSize="medium" color='primary' style={{ marginRight: 6, marginLeft: 12 }}/>}
                          label={getTranslateByKey("information.predefinedSearch")}
                          id='id-searchCartsPendingToAnswer-predefinedSearch'
                        />
                    </RenderTooltipMessage>
                  </div>
                </>
              }
            </S.Row>
            <KOGAreaButtons>
              <SecondaryButton
                type="submit"
                id="id-searchCartsPendingToAnswer-btn-submit">
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton
                onClick={handleClear}
                id="id-searchCartsPendingToAnswer-btn-clear">
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </KOGAreaButtons>
          </form>
        </Box>
        {
          <ModalSavePredefinedSearch
            open={openModalConfirmationSavePredefinedSearch}
            titleModal={getTranslateByKey("information.savePredefinedSearchTitle")}
            handleModalCancel={() => setOpenModalConfirmationSavePredefinedSearch(false)}
            handleModalConfirm={(description) => handleSavePredefinedSearch(description)}
            isLoading={loadingPredefinedSearch}
          />
        }
        {
          <ModalDeletePredefinedSearch
            open={openModalConfirmationDeletePredefinedSearch}
            predefinedSearchName={predefinedSearchToDelete?.value ?? ''}
            handleModalCancel={() => setOpenModalConfirmationDeletePredefinedSearch(false)}
            handleModalConfirm={handleDeletePredefinedSearch}
            isLoading={loadingPredefinedSearch}
          />
        }
        <PendingSearchGrid
          formFilters={formFilters}
          gridData={gridData}
          setGridData={setGridData}
          parameterValueDateRange={parameterRangeDays}
          initalDateForm={initalDateForm}
          INITIAL_FORM_STATE={INITIAL_FORM_STATE}
        />
      </S.Container>
    </ContainerScreen>
  );
}

export default withQueryparams(CartScreen);
